import algoliasearch from "algoliasearch/lite";
import tableSearch from "@/views/dashboard/shared/tableSearch/tableSearch.vue";
import _debounce from "lodash/debounce";
import { mapGetters } from "vuex";
import MiscApi from "@/services/api/misc";

const MiscApiService = new MiscApi(),
  SEARCH_DEBOUNCE = 500;

export default {
  components: {
    tableSearch,
  },

  methods: {
    onSearchQueryChange,
    search: _debounce(search, SEARCH_DEBOUNCE),
    getInvoices,
    getRetailers,
    getBrandRetailers,
    updateTableData,
    toggleSort,
    statusChangeCallback,
    initInvoicesIndex,
    retailerChangeCallback,
    brandChangeCallback,
    filterByRetailerId,
    filterByBrandId,
    navigateToRetailersProfile,
  },

  data() {
    return {
      loadingBrands: true,
      loadingRetailers: true,
      brandId: false,
      isAdmin: false,
      invoicesIndex: null,
      algoliaClient: algoliasearch(this.$env.algolia().appId, this.$env.algolia().token, {
        timeouts: {
          connect: this.$utils.constants.ALGOLIA_CONNECT_TIMEOUT,
          read: this.$utils.constants.ALGOLIA_READ_TIMEOUT,
        },
      }),
      query: "",
      selectedStatus: "",
      payments: null,
      statuses: [
        {
          name: "ALL",
        },
        {
          name: "PAID",
        },
        {
          name: "NOT PAID",
        },
        {
          name: "PAYMENT FAILED",
        },
        {
          name: "UNPROCESSABLE CHARGE",
        },
      ],
      pagination: {
        page: 1,
        limit: 12,
        totalCount: 0,
      },
      retailers: [],
      brands: [],
      selectedRetailer: "",
      selectedBrand: "",
    };
  },

  beforeMount() {
    if (this.user.role === "ADMIN") {
      this.isAdmin = true;
    } else {
      this.isAdmin = false;
      this.brandId = this.user.brand_id;
    }

    this.initInvoicesIndex();
    this.updateTableData(this.$route.query.page);

    if (this.isAdmin) {
      this.getRetailers();
    } else {
      this.getBrandRetailers();
    }
  },

  computed: {
    sort,
    sortName,
    sortValue,
    ...mapGetters({
      user: "userState",
    }),
  },
};

function onSearchQueryChange(val) {
  this.pagination.page = 1;
  this.query = val;

  this.payments = null;
  this.search();
}

function search() {
  this.updateTableData();
}

async function getInvoices(page) {
  const _self = this;
  if (page) {
    this.pagination.page = Number(page);
  }

  this.invoicesIndex.clearCache();
  this.invoicesIndex
    .search({
      query: this.query,
      page: this.pagination.page - 1,
      hitsPerPage: this.pagination.limit,
      facets: "*,brand_id",
      facetFilters: [
        this.brandId ? `brand_id:${this.brandId}` : [],
        this.filterByRetailerId(),
        this.isAdmin ? this.filterByBrandId() : [],
        this.selectedStatus === "" || this.selectedStatus.name === "ALL"
          ? []
          : `invoice_status:${this.selectedStatus.name.replace(/ /, "_")}`,
      ],
    })
    .then((response, err) => {
      if (err) {
        this.$swal("Attention", _self.$utils.formatErrorMessage("orders"));

        return;
      }

      this.pagination.totalCount = response.nbHits;
      this.payments = response.hits;
    });
}
async function getRetailers() {
  try {
    const { data } = await this.$utils.getAllRetailers();

    this.retailers = data.retailers_dropdown;
    this.retailers.unshift({
      name: "ALL",
    });
  } catch (error) {
    this.$swal("Attention", this.$utils.formatErrorMessage("retailers"));
  } finally {
    this.loadingRetailers = false;
  }
}

async function getBrandRetailers() {
  try {
    const { data } = await MiscApiService.getBrandRetailers(this.user.brand_id);

    this.retailers = data.retailers_dropdown;
    this.retailers.unshift({
      name: "ALL",
    });
  } catch (error) {
    this.$swal("Attention", this.$utils.formatErrorMessage("retailers"));
  } finally {
    this.loadingRetailers = false;
  }
}

function updateTableData(page) {
  if (page) {
    this.$router.replace({
      name: "payments",
      query: {
        page: page,
      },
    });
  }

  this.getInvoices(page);
}

function toggleSort(sort) {
  const descending = !this.sort || (this.sortName === sort && this.sortValue === "desc");
  this.$router.replace({
    query: {
      ...this.$route.query,
      sort: `${sort}:${descending ? "asc" : "desc"}`,
    },
  });

  this.initInvoicesIndex();
  this.updateTableData();
}

function statusChangeCallback(val) {
  if (val === "ALL") {
    this.selectedStatus = "";
  } else {
    this.selectedStatus = val;
  }

  this.pagination.page = 1;
  this.updateTableData();
}

function retailerChangeCallback(val) {
  if (val.name === "ALL") {
    this.selectedRetailer = "";
  } else {
    this.selectedRetailer = val.id;
  }

  this.pagination.page = 1;
  this.updateTableData();
}
function brandChangeCallback(val) {
  if (val.name === "ALL") {
    this.selectedBrand = "";
  } else {
    this.selectedBrand = val.name;
  }

  this.pagination.page = 1;
  this.updateTableData();
}

function initInvoicesIndex() {
  this.invoicesIndex = this.algoliaClient.initIndex(
    this.$env.getOrigin() + `_invoices${this.sort ? `_${this.sort.replace(":", "_")}` : ""}`
  );
}

function sort() {
  // The sort query string should look like this: ?sort=date:asc
  const sort = this.$route.query.sort;

  if (sort && /(\w*:\w*)/.test(sort)) {
    return sort;
  }

  return "";
}

function sortName() {
  return this.sort ? this.sort.split(":")[0] : "";
}

function sortValue() {
  return this.sort ? this.sort.split(":")[1] : "";
}

function filterByRetailerId() {
  return this.selectedRetailer === "" ? [] : `retailer_id:${this.selectedRetailer}`;
}

function filterByBrandId() {
  return this.selectedBrand === "" ? [] : `brand_name:${this.selectedBrand}`;
}

function navigateToRetailersProfile(retailerId) {
  this.$router.push({
    path: `/retailer/${retailerId}`,
  });
}
